import {
	buildRunScriptSceneActionFromFlowData,
	parseRunScriptFromSceneAction,
	type RunScriptCommand,
	type RunScriptFlowCardData,
} from 'src/helpers/device_script_action_helpers';
import { createHexToDecimalOrId } from 'src/helpers/utilities';
import type { FlowCardDefinition } from '../types';

const VERSION = '1.0.0';

const commandLabels: Record<RunScriptCommand, string> = {
	start: 'Run',
	stop: 'Stop',
	enable: 'Enable',
	disable: 'Disable',
};

function command(value: unknown): RunScriptCommand {
	return value === 'stop' || value === 'enable' || value === 'disable' ? value : 'start';
}

function scriptSummary(data: Record<string, any>): string {
	const verb = commandLabels[command(data.command)];
	const script = data.scriptName || (data.scriptId != null ? `script ${data.scriptId}` : 'a script');
	const device = data.deviceName || data.deviceRealId || data.deviceId;
	return device ? `${verb} ${script} on ${device}` : 'Choose a device and script';
}

function flowData(data: Record<string, any>): RunScriptFlowCardData | null {
	const deviceId = String(data.deviceId || data.deviceRealId || '');
	const deviceRealId = String(data.deviceRealId || deviceId).replace(/_\d+$/, '');
	const scriptId = Number(data.scriptId);
	if (!deviceRealId || !Number.isFinite(scriptId)) return null;
	return {
		deviceRealId,
		deviceId,
		deviceName: data.deviceName,
		roomId: data.roomId,
		roomName: data.roomName,
		channel: Number(data.channel ?? 0) || 0,
		scriptId,
		scriptName: data.scriptName,
		command: command(data.command),
	};
}

export const scriptActionCard: FlowCardDefinition = {
	type: 'scriptAction',
	version: VERSION,
	domain: 'core',
	role: 'action',
	category: 'actions',
	label: 'Run Script',
	description: 'Start, stop, enable, or disable a script on a Gen2+ device',
	icon: 'fa-brackets-curly',
	color: '#6366f1',
	deviceRequirement: 'script-capable',
	schema: {
		$schema: 'https://json-schema.org/draft/2020-12/schema',
		type: 'object',
		required: ['deviceRealId', 'scriptId', 'command'],
		properties: {
			deviceRealId: { type: 'string', minLength: 1 },
			deviceId: { type: 'string' },
			deviceName: { type: 'string' },
			roomId: { type: 'string' },
			roomName: { type: 'string' },
			channel: { type: 'number', minimum: 0 },
			scriptId: { type: 'number', minimum: 0 },
			scriptName: { type: 'string' },
			command: { type: 'string', enum: ['start', 'stop', 'enable', 'disable'] },
			delay: {
				type: 'object',
				properties: {
					value: { type: 'number', minimum: 0 },
					units: { type: 'string', enum: ['sec', 'min'] },
				},
			},
		},
	},
	fields: [
		{
			path: 'scriptId',
			label: 'Script configuration',
			type: 'script-configuration',
			helperText: 'Scripts are loaded from the selected Gen2+ device.',
		},
	],
	ports: { sources: ['output'] },
	defaults: () => ({
		deviceRealId: '',
		deviceId: '',
		channel: 0,
		command: 'start',
		delay: { value: 0, units: 'sec' },
		cardVersion: VERSION,
	}),
	summary: scriptSummary,
	normalize: (data) => ({ ...data, command: command(data.command), label: scriptSummary(data) }),
	validate: (data) => {
		const issues = [];
		if (!String(data.deviceRealId || data.deviceId || '').trim())
			issues.push({ path: 'deviceRealId', message: 'Select a device' });
		if (!Number.isFinite(Number(data.scriptId)))
			issues.push({ path: 'scriptId', message: 'Select a script' });
		return issues;
	},
	compile: (node) => {
		const configured = flowData(node.data);
		if (!configured) {
			if (node.data.sceneData) return { ...node.data.sceneData, _fid: node.id };
			if (node.data.originalAction) return { ...node.data.originalAction, _fid: node.id };
			return null;
		}
		return {
			...buildRunScriptSceneActionFromFlowData(
				configured,
				createHexToDecimalOrId(configured.deviceRealId),
			),
			_fid: node.id,
			_flow_card: 'scriptAction',
			_flow_card_version: VERSION,
		};
	},
	migrate: (data) => {
		const parsed = data.sceneData
			? parseRunScriptFromSceneAction(data.sceneData as any)
			: data.originalAction
				? parseRunScriptFromSceneAction(data.originalAction as any)
				: null;
		const migrated = {
			...data,
			...(parsed || {}),
			deviceRealId: String(
				parsed?.deviceRealId || data.deviceRealId || data.deviceId || '',
			).replace(/_\d+$/, ''),
			deviceId: parsed?.deviceId || data.deviceId || data.deviceRealId || '',
			channel: Number(parsed?.channel ?? data.channel ?? 0) || 0,
			scriptId: Number(parsed?.scriptId ?? data.scriptId),
			command: command(parsed?.command ?? data.command),
			delay: data.delay ?? { value: 0, units: 'sec' },
		};
		return { ...migrated, label: scriptSummary(migrated) };
	},
};
